import React, {ChangeEvent, ChangeEventHandler, FC, useEffect, useState} from 'react';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { getAllCertificates, postCertificateFile } from '../../common/async/AsyncCalls';
import { IUserState } from '../../common/state/profile/user';
import SimpleSpinner from '../loaders/spinners/SimpleSpinner';
import { BsFolderX } from 'react-icons/bs';
import { GrCertificate } from 'react-icons/gr';

type ICertificateUploadCardProps = {
    onSelect: (url: string) => void
};

const CertificateUploadCard : FC<ICertificateUploadCardProps> = ({onSelect}) => {    
    // states
    const token: string = useSelector(({user} : {
        user: IUserState
    }) => user.token);    
    const [isLoading, toggleLoading] = useState(false);
    const [isSubmitDisabled, toggleSubmitDisabled] = useState(true);
    const [fileSelected, setFileSelected] = useState<File | undefined>(undefined);
    const [errorText, setErrorText] = useState("");
    const [certificateList, setCertificateList] = useState(<React.Fragment/>);

    function renderCertificateList(list: any[]) {
        if (list.length > 0) {
            setCertificateList(
                <ul className="w-full p-2 overflow-y-scroll text-gray-700 bg-gray-200 rounded-lg max-h-5/6 dark:text-gray-200 dark:bg-gray-800">
                    {list.map((item: any, index: number)=> <li key={index} 
                    onClick={()=>onSelect(item.url)}
                    className="flex w-full px-4 py-2 rounded cursor-pointer hover:border-2 hover:border-primary-300 hover:dark:border-teal-400 hover:bg-gray-300 hover:dark:bg-gray-900">
                        <GrCertificate className="relative mt-1 mr-2 text-xl"/> <p>{item.name}</p>
                    </li>)}
                </ul>);
        } else {
            setCertificateList(
                <p className="flex flex-wrap w-full p-4 overflow-y-scroll text-gray-700 bg-gray-200 rounded-lg dark:text-gray-200 dark:bg-gray-800">
                    <BsFolderX className="relative mt-1 mr-4 text-xl"/> There are no certificates
                </p>
            );
        }
    }

    // upload file
    function handleFileUpload() {
        toggleLoading(true);
        console.log("Uploading file details: ", fileSelected);
        if (fileSelected) uploadCertificateFile(fileSelected);
    }

    //evt handlers
    function handleFileChange (event: ChangeEvent<HTMLInputElement>) {
        setErrorText("");        
        setFileSelected(undefined);
        console.log('File uploaded: ', event.currentTarget.files);
        if (!event.currentTarget.files || event.currentTarget.files.length == 0) return;
        const file: File = event.currentTarget.files[0];            
        if (file.type !== 'application/pdf') {
            setErrorText("*Only Files of type - *.pdf are supported");
            return;
        }
        setErrorText("");        
        setFileSelected(file);
    }
    
    const uploadCertificateFile = useCallback(async (file: File) => {
        try {
            toggleLoading(true);
            const formData = new FormData();
            formData.append('file', file);
            const result = await postCertificateFile(token, formData);
            // console.log('Response from server (file upload): ', result.data);            
            fetchCertificates();
        } catch (err) {
            setErrorText("*Error uploading file / file with similar name already exists!");
            console.log('Error uploading certificates', (err as Error).message);
        } finally {
            setFileSelected(undefined);
            toggleLoading(false);
        }
    },[token]);

    const fetchCertificates = useCallback(async ()=>{
        try {
            toggleLoading(true);
            const result = await getAllCertificates(token);
            // console.log('Certificate list: ', result.data);
            const list: any[] = result.data;
            renderCertificateList(list);
        } catch (err) {            
            console.log('Error fetching certificates', (err as Error).message);
        } finally {
            toggleLoading(false);
        }
    },[token]);

    useEffect(()=>{
        toggleSubmitDisabled(true);
        if (Boolean(fileSelected)) return toggleSubmitDisabled(false);        
    },[fileSelected]);

    // first time
    useEffect(()=>{
        fetchCertificates();
    },[]);

    return <React.Fragment>
        <section
        id="file-upload-section"
        className="flex flex-col justify-start p-4 my-3 mr-3 bg-white shadow-lg grow lg:w-1/2 rounded-xl dark:bg-gray-900">
        <p className="my-1 text-xs font-bold uppercase">Certificate files</p>    
        <SimpleSpinner loading={isLoading}/>
        <div className="flex justify-start my-4">
            {certificateList}
        </div>

        <div className="w-full">
            <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-teal-400" htmlFor="file_input">Upload cert file</label>
            <input
                onChange={handleFileChange}
                className="block w-full p-2 my-2 text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 dark:text-gray-400 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400" id="file_input" type="file"/>
            <p id="file-upload-validation" className="visible my-2 text-sm font-medium text-red-500">{errorText}</p>
        </div>

        <button
            disabled={isSubmitDisabled} 
            onClick={handleFileUpload}
            className="px-4 py-2 font-bold text-white bg-blue-500 border-b-4 border-blue-700 rounded cursor-pointer hover:bg-blue-400 disabled:bg-blue-200 disabled:cursor-not-allowed hover:border-blue-500 disabled:border-blue-200">
            Upload Certificate
        </button>
    </section>
    </React.Fragment>
};

export default CertificateUploadCard;